import { useEffect, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../../ampli';
import { usePostInquiryTractorMutation } from '../../../../hooks/api/inquiry/mutations/usePostInquiryTractor.mutation';
import { usePopup } from '../../../../hooks/popup/usePopup';
import { IProduct } from '../../../../interfaces/Product.interfaces';
import { Icon } from '../../../common/icon/Icon';
import { TelInput } from '../../../common/inputs/TelInput';
import { PopupConfirmModal } from '../../../common/modal/popup/PopupConfirmModal';

export type IProductForInquiryModal = Pick<
  IProduct,
  'id' | 'code' | 'title' | 'machine' | 'manufacturedYear' | 'operationTime'
>;

interface ProductInquiryModalProps {
  product: IProductForInquiryModal;
  pageType: 'pdp' | 'plp';
  onModalClose: () => void;
}

export const ProductInquiryModal = ({ product, pageType, onModalClose }: ProductInquiryModalProps) => {
  const navigate = useNavigate();
  const Popup = usePopup();

  const { mutate: postUserInquiryTractor } = usePostInquiryTractorMutation();

  const telInputRef = useRef<HTMLInputElement | null>(null);

  const [isPhoneNumberRequestModalOpen, setIsPhoneNumberRequestModalOpen] = useState<boolean>(true);
  const [isLoginRequestModalOpen, setIsLoginRequestModalOpen] = useState<boolean>(false);

  const [userPhoneNumber, setUserPhoneNumber] = useState<string>();

  useEffect(() => {
    if (isPhoneNumberRequestModalOpen && telInputRef.current) {
      telInputRef!.current!.focus();
    }
  }, [isPhoneNumberRequestModalOpen]);

  return (
    <>
      <PopupConfirmModal
        isShow={isPhoneNumberRequestModalOpen}
        hasCloseButton
        closeModal={() => setIsPhoneNumberRequestModalOpen(false)}
        onClose={() => {
          ampli.마켓매물상세페이지가격상담하기전화번호모달닫기버튼클릭({
            '매물 코드': product.code,
            page_type: pageType,
          });
          setIsLoginRequestModalOpen(true);
        }}
        title={'전화번호를 남겨주세요'}
        element={
          <RequestPhoneNumberContainer>
            <RequestPhoneNumberDescription>
              {'편하실 때 연락드릴게요.\n할인 상담받아보세요.'}
            </RequestPhoneNumberDescription>

            <TelInput
              inputValue={userPhoneNumber}
              placeholder={'예) 01012341234'}
              onChange={(value: string | undefined) => setUserPhoneNumber(value)}
              inputRef={telInputRef}
            />
          </RequestPhoneNumberContainer>
        }
        buttonArray={[
          {
            text: '남기기',
            onClick: () =>
              postUserInquiryTractor(
                {
                  reqBody: {
                    productId: product.id,
                    inquiryUserPhoneNumber: `${userPhoneNumber?.trim()}`,
                  },
                },
                {
                  onSuccess: () => {
                    ampli.마켓매물상세페이지가격상담하기전화번호모달남기기버튼클릭({
                      '매물 코드': product.code,
                      page_type: pageType,
                    });
                    // 전화번호 유효성 검사는 의도적으로 생략 https://gint-team.atlassian.net/browse/PVM-1736?focusedCommentId=12928
                    if (userPhoneNumber?.length === 11) {
                      setIsPhoneNumberRequestModalOpen(false);
                      ampli.pvmProductInquiry({
                        title: product.title,
                        type: 'Outbound',
                        ps: product.machine.engineOutputInPs,
                        manufacturedYear: product.manufacturedYear,
                        operationTime: product.operationTime,
                      });
                      ReactGA.event('pvm_product_inquiry', {
                        category: 'pvm',
                        action: 'pvm_product_inquiry',
                        value: 'Outbound',
                      });
                      Popup.toast('상담 요청이 완료되었습니다.');
                      onModalClose();
                    } else {
                      Popup.toast('전화번호를 제대로 입력하였는지 확인해 주세요.');
                    }
                  },
                },
              ),
            color: 'blue',
          },
        ]}
      />

      <PopupConfirmModal
        isShow={isLoginRequestModalOpen}
        hasCloseButton={false}
        closeModal={() => {
          setIsLoginRequestModalOpen(false);
          onModalClose();
        }}
        title={'로그인하면 회원할인이 적용되요'}
        buttonArray={[
          {
            text: '카카오톡으로 간편 로그인',
            onClick: () => {
              ampli.마켓매물상세페이지가격상담하기로그인모달로그인버튼클릭({
                '매물 코드': product.code,
                page_type: pageType,
              });
              ReactGA.event('pvm_click_login_button', {
                isModal: true,
              });
              navigate('/oauth/login');
            },
            color: 'yellow',
            icon: <Icon name={'KakaoTalk'} width={24} height={24} />,
          },
          {
            text: '닫기',
            onClick: () => {
              ampli.마켓매물상세페이지가격상담하기로그인모달닫기버튼클릭({
                '매물 코드': product.code,
                page_type: pageType,
              });
              setIsLoginRequestModalOpen(false);
              onModalClose();
            },
            color: 'gray',
          },
        ]}
      />
    </>
  );
};

const RequestPhoneNumberContainer = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;

  gap: 24px;
`;

const RequestPhoneNumberDescription = styled.pre`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */
`;

const RequestPhoneNumberBlueText = styled.span`
  color: var(--Light-Scheme-Color-primary-blue, #196eff);

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%;
`;
